import type { ReactNode } from 'react';

import { Card, CardContent } from '@pesto/ui/components/Card';
import { Separator } from '@pesto/ui/components/Separator';
import { formatDateOnly, formatRate } from '@pesto/utils';
import { Landmark } from 'lucide-react';

import type { EntityFieldGridConfig } from '../../EntityProfile/types/EntityFieldGrid.types';

import { EntityFieldGrid } from '../../EntityProfile/components/EntityFieldGrid';
import { EntityHeader } from '../../EntityProfile/components/EntityHeader';

interface LoanDetailsProps {
  originationDate?: string | null;
  onboardedDate?: string | null;
  initialBalance?: number | null;
  aprMargin?: string | null;
  currentApr?: string | null;
  currentPrimeRate?: string | null;
  isVariableApr?: boolean | null;
  promoEndDate?: string | null;
  gracePeriodDays?: number | null;
  children?: ReactNode;
}

const getPromoStatus = (promoEndDate?: string | null) => {
  if (!promoEndDate) return null;
  const isActive = new Date(promoEndDate) > new Date();

  return { isActive };
};

export const LoanDetails = ({
  originationDate,
  onboardedDate,
  initialBalance,
  aprMargin,
  currentApr,
  currentPrimeRate,
  isVariableApr,
  promoEndDate,
  gracePeriodDays,
  children,
}: LoanDetailsProps) => {
  const promoStatus = getPromoStatus(promoEndDate);
  const hasPromo = Boolean(promoEndDate);

  const originationConfig: EntityFieldGridConfig = {
    columns: 2,
    fields: [
      { kind: 'date', label: 'Origination Date', value: originationDate, format: 'd MMMM yyyy' },
      { kind: 'date', label: 'Onboarded Date', value: onboardedDate },
      ...(initialBalance != null
        ? [{ kind: 'currency' as const, label: 'Initial Balance', value: initialBalance }]
        : []),
    ],
  };

  const rateConfig: EntityFieldGridConfig = {
    columns: 2,
    fields: [
      { kind: 'text', label: 'APR Type', value: isVariableApr == null ? null : isVariableApr ? 'Variable' : 'Fixed' },
      { kind: 'text', label: 'Current APR', value: formatRate(currentApr) },
      { kind: 'text', label: 'APR Margin', value: formatRate(aprMargin) },
      { kind: 'text', label: 'Current Prime Rate', value: formatRate(currentPrimeRate) },
    ],
  };

  const promoConfig: EntityFieldGridConfig | null =
    promoStatus || (!hasPromo && gracePeriodDays != null)
      ? {
          columns: 2,
          fields: [
            ...(!hasPromo && gracePeriodDays != null
              ? [{ kind: 'text' as const, label: 'Grace Period', value: `${gracePeriodDays} days` }]
              : []),
            ...(promoStatus
              ? [
                  {
                    kind: 'badge' as const,
                    label: 'Teaser Rate Status',
                    value: promoStatus.isActive ? 'Active' : 'Expired',
                    variant: promoStatus.isActive ? ('default' as const) : ('secondary' as const),
                  },
                  {
                    kind: 'text' as const,
                    label: 'Teaser Rate End Date',
                    value: formatDateOnly(promoEndDate!),
                  },
                ]
              : []),
          ],
        }
      : null;

  return (
    <Card>
      <EntityHeader
        config={{
          avatar: { icon: Landmark },
          title: 'Loan Details',
          description: 'Account origination, rates, and status',
        }}
      />
      <CardContent className="space-y-4 pt-0">
        <EntityFieldGrid config={originationConfig} />
        <Separator />
        <EntityFieldGrid config={rateConfig} />
        {promoConfig && (
          <>
            <Separator />
            <EntityFieldGrid config={promoConfig} />
          </>
        )}
        {children && (
          <>
            <Separator />
            {children}
          </>
        )}
      </CardContent>
    </Card>
  );
};
